---
title: "How to compress a PDF to 5 MB"
description: "Need to get a PDF under a 5 MB upload limit? Here is what actually shrinks, what does not, and what to do when a file will not shrink."
locale: "en"
date: "2026-05-16"
tags: ["pdf", "compress", "upload"]
relatedTools: ["szhat"]
translations:
  ru: "kak-szhat-pdf-do-5-mb-dlya-gosuslug"
faq:
  - q: "Can I guarantee a PDF under 5 MB on the first try?"
    a: "Not always. Text-heavy PDFs with vector graphics are often already compact, so there is not much to remove. Scanned PDFs and photo-heavy files usually shrink much more."
  - q: "Does the file go to a server?"
    a: "No. The tool compresses the PDF right in your browser, and the file stays on your device."
  - q: "Why did the size barely change?"
    a: "That usually means the PDF already contains mostly text, tables and vector objects instead of heavy raster images. In that case there is very little left to compress."
  - q: "Will the quality get worse?"
    a: "It depends on the file and the mode. With scanned pages, some quality loss is the trade-off for a smaller file. Always open the result and check small text before uploading it."
---

import { SzhatForm } from '@/pages/szhat/SzhatForm'

The upload form says "maximum 5 MB" and your PDF weighs 8.7. Of course you
notice it at the very end, after filling out everything else. People react to
this by printing to PDF again, exporting the file three different ways, then
hoping one of them somehow comes out smaller. It usually does not.

The tool below is the faster route. Drop in the file and see how much size it
can shave off. If the standard level is enough, you are done. If it is not,
try the strong level.

<div className="my-8 rounded-xl border bg-card p-5 shadow-sm">
  <SzhatForm />
</div>

## Where the extra megabytes usually come from

If a PDF came straight out of Word or Google Docs, it is often boring in a good
way. Text, fonts, vector lines, maybe a bit of graphics. Files like that may
already sit at 400-800 KB, and there is not much left to squeeze.

Scans are a different story. Especially color scans. Especially from office
machines that default to "better quality" and never mention the cost. On the
outside it is one PDF. Inside it is a stack of images, sometimes very heavy
ones. Stamps, gray paper background, scanner noise, crooked pages, phone photos
instead of clean document exports - that is where the megabytes usually hide.

## What happens when the file is compressed

Compression runs locally in your browser. That is not a teaser
mode. For a lot of everyday scans it is enough.

Some PDFs still refuse to cooperate. Old fax-style scans, odd image formats,
documents that somebody already compressed badly once before you got them - all
of that can lead to weak results. Try the strong level then, and if that does
not help, remove extra pages or export the document again with lower image
quality.

That distinction is worth keeping honest. Smallpdf, iLovePDF, PDF24 - with many
tools like that the file goes to their server immediately. Here it does not:
everything happens in the browser. For a cafeteria menu this is
probably irrelevant. For a passport scan, contract or power of attorney it is
not.

## The practical way to get under 5 MB

If the first pass leaves you at 5.9 MB, that is not a disaster. It usually just
means you need the second pass. The harder cases are large color scans with lots
of pages, stamps and noisy backgrounds. There is no magic promise there.
Sometimes rescanning at 150-200 dpi really is the faster fix.

If the size barely changes, do not assume the tool failed. The PDF may already
be compact. Text, tables and vectors do not compress forever. At that point it
is smarter to check whether you are uploading unnecessary pages, duplicates or
full-color attachments that did not need to be full-color in the first place.

The non-dramatic workflow is simple: try the standard level first, use the strong
level only if needed, then open the result and inspect the smallest text. If
the file drops below 5 MB after that, the argument with the upload form is
usually over.
